import { ui } from "@/components/ui";
import { useEntryValuesMutation } from "@/features/entries/api/upsertEntryValues";
import useExtractEntryValue from "@/features/entries/hooks/useExtractEntryValue";
import InputSwitch from "@/features/inputs/components/InputSwitch";
import { createDefaultValues } from "@/features/pages/utils/createDefaultValues";
import { createValidationSchema } from "@/features/pages/utils/createValidationSchema";
import { formatEntryValues } from "@/features/pages/utils/formatEntryValues";
import type { EntryValue } from "@/types/database/entries";
import type { InputT } from "@/types/database/forms";
import { type SubmitHandler, useForm, type z, zodResolver } from "@/utils/form";
import EditDialog from "../../../components/common/EditDialog";
import { EntryValueSwitch } from "../../entry-values/components/EntryValueSwitch";
import { useExtractObservationEntryValue } from "../hooks/useExtractObservationEntryValue";

interface ObservationEntryValueEditDialogProps {
	diaryId: string;
	observationId: string;
	entryId: string;
	entryValueId: string;
}

export function ObservationEntryValueEditDialog({
	diaryId,
	observationId,
	entryId,
	entryValueId,
}: ObservationEntryValueEditDialogProps) {
	const entryValue = useExtractObservationEntryValue(
		diaryId,
		observationId,
		entryId,
		entryValueId,
	);

	const upsertEntryValue = useEntryValuesMutation(diaryId);
	const isSubmitting = upsertEntryValue.isPending;

	// Only create schema if entryValue exists
	const SchemaZ = entryValue?.inputs
		? createValidationSchema([entryValue.inputs as InputT])
		: createValidationSchema([]);
	type SchemaT = z.infer<typeof SchemaZ>;

	// Only create defaults if entryValue exists
	const defaults = entryValue?.inputs
		? createDefaultValues([entryValue.inputs as InputT])
		: {};

	const form = useForm<SchemaT>({
		resolver: zodResolver(SchemaZ),
		defaultValues: defaults,
	});

	const onSubmit: SubmitHandler<SchemaT> = async (formData) => {
		if (!entryValue?.inputs) return;

		const formatted = formatEntryValues(formData, [entryValue.inputs], entryId);
		const formattedEntryValues = formatted.map((f) => ({
			...f,
			entry_value_id: entryValueId,
		}));
		upsertEntryValue.mutate(formattedEntryValues as EntryValue["Update"][]);
	};

	return (
		<EditDialog
			key={entryValue?.entry_value_id}
			show={true}
			info={
				<EntryValueSwitch
					className="border-b-0"
					size="sm"
					entryValue={entryValue}
				/>
			}
		>
			<div className="flex flex-col gap-4">
				<ui.Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="form">
						{entryValue?.inputs && (
							<InputSwitch input={entryValue.inputs} form={form} />
						)}
						<ui.DialogClose>
							<ui.Button size="md" disabled={isSubmitting}>
								{isSubmitting ? "Saving..." : "Save"}
							</ui.Button>
						</ui.DialogClose>
					</form>
				</ui.Form>
			</div>
		</EditDialog>
	);
}
